import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogOut, RefreshCw, ArrowLeft, Phone, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PHONES } from "@/lib/site";
import { PortalAuth } from "@/components/portal/PortalAuth";
import { VehicleCard } from "@/components/portal/VehicleCard";

const PortalHeader = ({ user, onLogout, onRefresh }) => (
  <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0C10]/85 backdrop-blur-xl" data-testid="portal-header">
    <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 lg:px-8">
      <Link to="/" className="flex items-center gap-3" data-testid="portal-logo">
        <span className="font-display text-xl tracking-[0.15em] text-slate-50">SAGITTARIUS</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">Customer Portal</span>
      </Link>
      <div className="flex items-center gap-3">
        <span className="hidden font-mono text-xs text-slate-500 sm:block" data-testid="portal-user-email">{user.email}</span>
        <button onClick={onRefresh} data-testid="portal-refresh-btn" className="border border-white/15 p-2 text-slate-400 transition-colors hover:border-amber-600 hover:text-amber-500"><RefreshCw size={15} /></button>
        <button onClick={onLogout} data-testid="portal-logout-btn" className="flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 transition-colors hover:border-red-500/60 hover:text-red-400">
          <LogOut size={13} /> Sign out
        </button>
      </div>
    </div>
  </header>
);

const Dashboard = ({ user, onLogout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customer/vehicles");
      setData(data);
    } catch {
      setData({ vehicles: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="App min-h-screen" data-testid="portal-dashboard">
      <PortalHeader user={user} onLogout={onLogout} onRefresh={load} />
      <main className="mx-auto max-w-6xl px-5 py-12 lg:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-500">Welcome back</p>
        <h1 className="mt-3 font-display text-4xl uppercase leading-[0.95] tracking-tight text-slate-50 sm:text-5xl" data-testid="portal-greeting">
          {user.name || "Your garage"}
        </h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-400">
          Every vehicle we've logged under <span className="text-slate-200">{user.email}</span>{user.phone ? <> or <span className="text-slate-200">{user.phone}</span></> : ""} — full service history, Oil Trac status, photos and invoices.
        </p>

        {loading && <p className="py-20 text-center font-mono text-xs uppercase tracking-[0.25em] text-slate-600"><Loader2 size={16} className="mx-auto animate-spin" /></p>}

        {!loading && data?.vehicles?.length === 0 && (
          <div className="mt-12 border border-white/10 bg-[#12151C] p-10 text-center" data-testid="portal-empty">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400">No vehicles on file yet</p>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate-500">
              We match records to the email and phone you used at the workshop. If you've been in before, call us and we'll link your history to this account.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-6">
              {PHONES.map((p) => (
                <a key={p.id} href={p.tel} className="inline-flex items-center gap-2 font-mono text-sm text-amber-500" data-testid={`portal-empty-phone-${p.id}`}><Phone size={14} /> {p.display}</a>
              ))}
            </div>
          </div>
        )}

        {!loading && data?.vehicles?.length > 0 && (
          <div className="mt-12 space-y-8" data-testid="portal-vehicles">
            {data.vehicles.map((v, i) => <VehicleCard key={v.rego} vehicle={v} index={i} />)}
          </div>
        )}
      </main>
    </div>
  );
};

export default function CustomerPortal() {
  const { user, logout } = useAuth();

  if (user === null)
    return <div className="App flex min-h-screen items-center justify-center"><Loader2 size={20} className="animate-spin text-amber-500" /></div>;

  if (!user)
    return (
      <div className="App flex min-h-screen flex-col items-center justify-center gap-8 px-5 py-16" data-testid="portal-auth-page">
        <PortalAuth />
        <Link to="/" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-amber-500" data-testid="portal-back-home">
          <ArrowLeft size={12} /> Back to site
        </Link>
      </div>
    );

  return <Dashboard user={user} onLogout={logout} />;
}
